import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api from "../lib/api";
import { tzList, detectTimezone } from "../lib/timezones";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";

const DAY_KEYS = ["0", "1", "2", "3", "4", "5", "6"];

export function BookingConfig() {
  const { t, lang } = useLang();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/booking/settings").then((r) => setS(normalize(r.data))).catch(() => setS(normalize({}))); }, []);
  const normalize = (d) => ({
    booking_enabled: d.booking_enabled || false,
    timezone: d.timezone || detectTimezone(),
    weekly_availability: DAY_KEYS.reduce((a, k) => ({ ...a, [k]: (d.weekly_availability || {})[k] || [] }), {}),
    appointment_types: d.appointment_types?.length ? d.appointment_types : [{ label: "", minutes: 50, modality: "online", is_default: true }],
    buffer_before: d.buffer_before || 0, buffer_after: d.buffer_after || 0,
    min_notice_hours: d.min_notice_hours ?? 24, booking_horizon_days: d.booking_horizon_days ?? 30,
    blocked: d.blocked || [], location: d.location || "", online_info: d.online_info || "",
    cancellation_policy: d.cancellation_policy || "", reminders: d.reminders || { advance: true, same_day: true },
  });
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const addWindow = (day) => set("weekly_availability", { ...s.weekly_availability, [day]: [...s.weekly_availability[day], { start: "09:00", end: "12:00" }] });
  const updWindow = (day, i, k, v) => { const w = [...s.weekly_availability[day]]; w[i] = { ...w[i], [k]: v }; set("weekly_availability", { ...s.weekly_availability, [day]: w }); };
  const rmWindow = (day, i) => set("weekly_availability", { ...s.weekly_availability, [day]: s.weekly_availability[day].filter((_, x) => x !== i) });

  const addType = () => set("appointment_types", [...s.appointment_types, { label: "", minutes: 50, modality: "online", is_default: false }]);
  const updType = (i, k, v) => { const a = s.appointment_types.map((tp, x) => x === i ? { ...tp, [k]: v } : (k === "is_default" && v ? { ...tp, is_default: false } : tp)); set("appointment_types", a); };
  const rmType = (i) => set("appointment_types", s.appointment_types.filter((_, x) => x !== i));

  const addBlock = () => set("blocked", [...s.blocked, { date: "", start: "", end: "", label: "" }]);
  const updBlock = (i, k, v) => { const b = [...s.blocked]; b[i] = { ...b[i], [k]: v }; set("blocked", b); };
  const rmBlock = (i) => set("blocked", s.blocked.filter((_, x) => x !== i));

  const save = async () => {
    setSaving(true);
    try {
      const clean = { ...s, appointment_types: s.appointment_types.filter((tp) => tp.label?.trim()), blocked: s.blocked.filter((b) => b.date) };
      if (clean.appointment_types.length && !clean.appointment_types.some((tp) => tp.is_default)) clean.appointment_types[0].is_default = true;
      const { data } = await api.put("/booking/settings", clean);
      setS(normalize(data));
      toast.success(t("booking.settings_saved"));
    } catch { toast.error(t("booking.settings_err")); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="py-10 text-center text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-8" data-testid="booking-config">
      <label className="flex items-center gap-3 rounded-xl border border-border bg-white p-4">
        <Checkbox checked={s.booking_enabled} onCheckedChange={(v) => set("booking_enabled", !!v)} data-testid="booking-enabled" />
        <span className="text-sm font-medium text-slateink">{t("booking.enable_booking")}</span>
      </label>

      <div>
        <Label>{t("booking.your_timezone")}</Label>
        <Select value={s.timezone} onValueChange={(v) => set("timezone", v)}>
          <SelectTrigger className="mt-1.5 max-w-xs" data-testid="booking-timezone"><SelectValue /></SelectTrigger>
          <SelectContent>{tzList().map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
        </Select>
        <p className="mt-1 text-xs text-muted-foreground">{t("booking.avail_tz_note")}</p>
      </div>

      <div>
        <h3 className="font-display font-semibold text-slateink">{t("booking.weekly_avail")}</h3>
        <div className="mt-3 space-y-3">
          {DAY_KEYS.map((day) => (
            <div key={day} className="rounded-xl border border-border bg-white p-3" data-testid={`day-${day}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slateink">{t("booking.days")[Number(day)]}</span>
                <Button type="button" size="sm" variant="ghost" onClick={() => addWindow(day)} data-testid={`add-window-${day}`}><Plus className="h-3.5 w-3.5" /></Button>
              </div>
              {s.weekly_availability[day].length === 0 && <p className="text-xs text-muted-foreground">{t("booking.unavailable")}</p>}
              {s.weekly_availability[day].map((w, i) => (
                <div key={i} className="mt-2 flex items-center gap-2">
                  <Input type="time" value={w.start} onChange={(e) => updWindow(day, i, "start", e.target.value)} className="w-32" data-testid={`win-start-${day}-${i}`} />
                  <span className="text-muted-foreground">–</span>
                  <Input type="time" value={w.end} onChange={(e) => updWindow(day, i, "end", e.target.value)} className="w-32" data-testid={`win-end-${day}-${i}`} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => rmWindow(day, i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-slateink">{t("booking.appt_types")}</h3>
          <Button type="button" size="sm" variant="outline" onClick={addType} data-testid="add-appt-type"><Plus className="mr-1 h-3.5 w-3.5" />{t("booking.add")}</Button>
        </div>
        <div className="mt-3 space-y-3">
          {s.appointment_types.map((tp, i) => (
            <div key={i} className="grid gap-2 rounded-xl border border-border bg-white p-3 sm:grid-cols-[1fr_100px_140px_auto]" data-testid={`appt-type-${i}`}>
              <Input placeholder={t("booking.type_label_ph")} value={tp.label || ""} onChange={(e) => updType(i, "label", e.target.value)} data-testid={`type-label-${i}`} />
              <Input type="number" min="10" step="5" value={tp.minutes} onChange={(e) => updType(i, "minutes", parseInt(e.target.value) || 0)} data-testid={`type-minutes-${i}`} />
              <Select value={tp.modality} onValueChange={(v) => updType(i, "modality", v)}>
                <SelectTrigger data-testid={`type-modality-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">{t("common.online")}</SelectItem>
                  <SelectItem value="in_person">{t("common.in_person")}</SelectItem>
                  <SelectItem value="both">{t("common.both")}</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-xs"><Checkbox checked={tp.is_default} onCheckedChange={(v) => updType(i, "is_default", !!v)} />{t("booking.default_short")}</label>
                <Button type="button" size="icon" variant="ghost" onClick={() => rmType(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label>{t("booking.buffer_before")}</Label><Input type="number" min="0" value={s.buffer_before} onChange={(e) => set("buffer_before", parseInt(e.target.value) || 0)} data-testid="buffer-before" /></div>
        <div><Label>{t("booking.buffer_after")}</Label><Input type="number" min="0" value={s.buffer_after} onChange={(e) => set("buffer_after", parseInt(e.target.value) || 0)} data-testid="buffer-after" /></div>
        <div><Label>{t("booking.min_notice")}</Label><Input type="number" min="0" value={s.min_notice_hours} onChange={(e) => set("min_notice_hours", parseInt(e.target.value) || 0)} data-testid="min-notice" /></div>
        <div><Label>{t("booking.booking_horizon")}</Label><Input type="number" min="1" value={s.booking_horizon_days} onChange={(e) => set("booking_horizon_days", parseInt(e.target.value) || 1)} data-testid="horizon" /></div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-slateink">{t("booking.blocked_dates")}</h3>
          <Button type="button" size="sm" variant="outline" onClick={addBlock} data-testid="add-block"><Plus className="mr-1 h-3.5 w-3.5" />{t("booking.add")}</Button>
        </div>
        <div className="mt-3 space-y-2">
          {s.blocked.map((b, i) => (
            <div key={i} className="grid gap-2 rounded-xl border border-border bg-white p-3 sm:grid-cols-[150px_110px_110px_1fr_auto]" data-testid={`block-${i}`}>
              <Input type="date" value={b.date || ""} onChange={(e) => updBlock(i, "date", e.target.value)} data-testid={`block-date-${i}`} />
              <Input type="time" value={b.start || ""} onChange={(e) => updBlock(i, "start", e.target.value)} placeholder={t("booking.all_day")} />
              <Input type="time" value={b.end || ""} onChange={(e) => updBlock(i, "end", e.target.value)} />
              <Input value={b.label || ""} onChange={(e) => updBlock(i, "label", e.target.value)} placeholder={t("booking.internal_note_priv")} />
              <Button type="button" size="icon" variant="ghost" onClick={() => rmBlock(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label>{t("booking.location_inperson")}</Label><Input value={s.location || ""} onChange={(e) => set("location", e.target.value)} data-testid="booking-location" /></div>
        <div><Label>{t("booking.online_info")}</Label><Input value={s.online_info || ""} onChange={(e) => set("online_info", e.target.value)} data-testid="booking-online-info" /></div>
      </div>
      <div>
        <Label>{t("booking.cancel_policy_opt")}</Label>
        <Textarea rows={2} value={s.cancellation_policy || ""} onChange={(e) => set("cancellation_policy", e.target.value)} data-testid="cancel-policy" />
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={s.reminders.advance} onCheckedChange={(v) => set("reminders", { ...s.reminders, advance: !!v })} data-testid="rem-advance" />{t("booking.advance_reminder")}</label>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={s.reminders.same_day} onCheckedChange={(v) => set("reminders", { ...s.reminders, same_day: !!v })} data-testid="rem-sameday" />{t("booking.sameday_reminder")}</label>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="save-booking-settings">
        <Save className="mr-2 h-4 w-4" />{saving ? t("common.saving") : t("common.save")}
      </Button>
    </div>
  );
}
